/**
 * HeaderLanguageSelector — globo + idioma alternativo, no Header.
 *
 * Toggle de idioma do mockup: globo dourado + o idioma para o qual o clique
 * alterna, não o atual (só renderiza quando o backend é multilíngue —
 * plone.app.multilingual com 2+ idiomas). Linka para a tradução do conteúdo
 * atual quando ela existe, senão para a raiz do idioma alvo. O rótulo é o
 * código de 3 letras do nome nativo — "Eng", "Por".
 *
 * Também aparece no menu lateral, só no mobile: na tela estreita o header
 * não comporta as duas marcas + idioma (mockup de 2026-09-30), então a troca
 * de idioma vai para dentro do drawer — ver docs/specs/header-nav.md §2.3c.
 *
 * `variant`:
 *   - `compact` (padrão, header): código de 3 letras — "Eng", "Por".
 *   - `full` (drawer): nome nativo completo — "English", "Português".
 */
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import langmap from '@plone/volto/helpers/LanguageMap/LanguageMap';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';
import globeSVG from '../../icons/globe.svg';

const messages = defineMessages({
  switchLanguageTo: {
    id: 'Switch to',
    defaultMessage: 'Switch to',
  },
});

export type HeaderLanguageSelectorProps = {
  variant?: 'compact' | 'full';
  className?: string;
  /** Chamado ao clicar num idioma (o drawer usa para se fechar). */
  onNavigate?: () => void;
};

const HeaderLanguageSelector = ({
  variant = 'compact',
  className,
  onNavigate,
}: HeaderLanguageSelectorProps) => {
  const intl = useIntl();
  const currentLang = useSelector((s: any) => s.intl.locale);
  const translations = useSelector(
    (s: any) => s.content.data?.['@components']?.translations?.items,
  );
  const isMultilingual = useSelector(
    (s: any) => s.site.data?.features?.multilingual,
  );
  const availableLanguages = useSelector(
    (s: any) => s.site.data?.['plone.available_languages'],
  );

  const otherLanguages = availableLanguages?.filter(
    (lang: string) => toReactIntlLang(lang) !== currentLang,
  );

  if (!isMultilingual || !otherLanguages?.length) {
    return null;
  }

  return (
    <div className={className ?? 'header__tools'}>
      <span className="header__globe" aria-hidden="true">
        <Icon name={globeSVG} size="20px" />
      </span>
      <div className="language-selector">
        {otherLanguages.map((lang: string) => {
          const nativeName =
            langmap[lang as keyof typeof langmap]?.nativeName || lang;
          const translation = translations?.find(
            (t: { language: string; '@id': string }) => t.language === lang,
          );
          return (
            <Link
              key={`language-selector-${lang}`}
              to={
                translation ? flattenToAppURL(translation['@id']) : `/${lang}`
              }
              title={nativeName}
              onClick={onNavigate}
              aria-label={`${intl.formatMessage(
                messages.switchLanguageTo,
              )} ${nativeName.toLowerCase()}`}
            >
              {variant === 'full' ? nativeName : nativeName.slice(0, 3)}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HeaderLanguageSelector;
